import * as THREE from 'three';

export type FloorPickup = {
  mesh: THREE.Mesh;
  beam: THREE.Mesh;
  kind: 'ammo' | 'shield' | 'mat' | 'forage' | 'rift';
  amount: number;
  taken: boolean;
  collecting: boolean;
  collectT: number;
  baseY: number;
  baseScale: number;
};

const KIND_COLOR: Record<FloorPickup['kind'], { color: number; emissive: number; beam: number }> = {
  ammo: { color: 0xffdd33, emissive: 0xaa8800, beam: 0xffee55 },
  // shield → rare (blue)
  shield: { color: 0x4488ff, emissive: 0x2255cc, beam: 0x66aaff },
  // mat → common (gray)
  mat: { color: 0x888888, emissive: 0x555555, beam: 0xaaaaaa },
  // forage → uncommon (green)
  forage: { color: 0x00cc44, emissive: 0x008833, beam: 0x44ff88 },
  rift: { color: 0xcc44ff, emissive: 0x9922cc, beam: 0xee88ff },
};

const BEAM_HEIGHT = 2.4;
const BEAM_RADIUS = 0.025;
const COLLECT_FLOAT_DURATION = 0.38;

export class FpsFloorPickupField {
  private readonly pickups: FloorPickup[] = [];
  private readonly bobPhase: number[] = [];
  private readonly beamGeo = new THREE.CylinderGeometry(BEAM_RADIUS, BEAM_RADIUS, BEAM_HEIGHT, 8);

  constructor(
    _scene: THREE.Scene,
    sceneRoot: THREE.Group,
    positions: { x: number; z: number; kind: FloorPickup['kind']; amount: number }[],
  ) {
    let i = 0;
    for (const p of positions) {
      const r = p.kind === 'forage' ? 0.28 : p.kind === 'rift' ? 0.4 : 0.35;
      const geom = new THREE.IcosahedronGeometry(r, 0);
      const pal = KIND_COLOR[p.kind];
      const mat = new THREE.MeshStandardMaterial({
        color: pal.color,
        emissive: pal.emissive,
        emissiveIntensity: 1.05,
        metalness: 0.15,
        roughness: 0.35,
      });
      const mesh = new THREE.Mesh(geom, mat);
      mesh.position.set(p.x, 0.35, p.z);
      mesh.castShadow = true;
      mesh.receiveShadow = true;
      sceneRoot.add(mesh);

      const beamMat = new THREE.MeshBasicMaterial({
        color: pal.beam,
        transparent: true,
        opacity: 0.38,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
        side: THREE.DoubleSide,
      });
      const beam = new THREE.Mesh(this.beamGeo, beamMat);
      beam.position.set(p.x, 0.35 + BEAM_HEIGHT * 0.5, p.z);
      sceneRoot.add(beam);

      this.pickups.push({
        mesh,
        beam,
        kind: p.kind,
        amount: p.amount,
        taken: false,
        collecting: false,
        collectT: 0,
        baseY: 0.35,
        baseScale: 1,
      });
      this.bobPhase.push(i * 0.7);
      i += 1;
    }
  }

  /** Call each frame so pickups bob vertically and animate collection. */
  update(timeSeconds: number, dt = 1 / 60): void {
    const t = timeSeconds;
    for (let i = 0; i < this.pickups.length; i++) {
      const pick = this.pickups[i]!;
      if (pick.taken) continue;

      if (pick.collecting) {
        pick.collectT += dt;
        const u = Math.min(1, pick.collectT / COLLECT_FLOAT_DURATION);
        const ease = 1 - (1 - u) ** 2;
        pick.mesh.position.y = pick.baseY + ease * 1.1;
        pick.beam.position.y = pick.mesh.position.y + BEAM_HEIGHT * 0.5;
        const pulse = 1 + Math.sin(t * 14) * 0.06;
        const shrink = 1 - u * 0.55;
        pick.mesh.scale.setScalar(pick.baseScale * pulse * shrink);
        pick.beam.scale.setScalar(Math.max(0.05, 1 - u * 0.85));
        const meshMat = pick.mesh.material as THREE.MeshStandardMaterial;
        meshMat.emissiveIntensity = 1.05 + (1 - u) * 0.55;
        const beamMat = pick.beam.material as THREE.MeshBasicMaterial;
        beamMat.opacity = 0.38 * (1 - u);
        if (pick.collectT >= COLLECT_FLOAT_DURATION) {
          this.removePickup(pick);
        }
        continue;
      }

      const phase = this.bobPhase[i] ?? 0;
      pick.mesh.position.y = pick.baseY + Math.sin(t * 2.4 + phase) * 0.12;
      pick.beam.position.y = pick.mesh.position.y + BEAM_HEIGHT * 0.5;
      const pulse = 1 + Math.sin(t * 3.6 + phase) * 0.08;
      pick.mesh.scale.setScalar(pick.baseScale * pulse);
      const beamMat = pick.beam.material as THREE.MeshBasicMaterial;
      beamMat.opacity = 0.3 + Math.sin(t * 4.2 + phase) * 0.1;
    }
  }

  tryCollect(px: number, py: number, pz: number, radius: number): { kind: FloorPickup['kind']; amount: number } | null {
    if (!Number.isFinite(px) || !Number.isFinite(py) || !Number.isFinite(pz) || !Number.isFinite(radius) || radius <= 0) {
      return null;
    }
    const r2 = radius * radius;
    for (const pick of this.pickups) {
      if (pick.taken || pick.collecting) continue;
      const dx = px - pick.mesh.position.x;
      const dz = pz - pick.mesh.position.z;
      if (dx * dx + dz * dz > r2) continue;
      return this.takePickup(pick);
    }
    return null;
  }

  private takePickup(pick: FloorPickup): { kind: FloorPickup['kind']; amount: number } {
    pick.taken = true;
    pick.collecting = true;
    pick.collectT = 0;
    pick.baseY = pick.mesh.position.y;
    return { kind: pick.kind, amount: pick.amount };
  }

  private removePickup(pick: FloorPickup): void {
    pick.mesh.removeFromParent();
    pick.beam.removeFromParent();
    pick.mesh.geometry.dispose();
    pick.beam.geometry.dispose();
    const meshMat = pick.mesh.material;
    if (Array.isArray(meshMat)) meshMat.forEach((m) => m.dispose());
    else meshMat.dispose();
    const beamMat = pick.beam.material;
    if (Array.isArray(beamMat)) beamMat.forEach((m) => m.dispose());
    else beamMat.dispose();
    const idx = this.pickups.indexOf(pick);
    if (idx >= 0) {
      this.pickups.splice(idx, 1);
      this.bobPhase.splice(idx, 1);
    }
  }

  dispose(): void {
    for (const pick of this.pickups) {
      pick.mesh.removeFromParent();
      pick.beam.removeFromParent();
      pick.mesh.geometry.dispose();
      const meshMat = pick.mesh.material;
      if (Array.isArray(meshMat)) meshMat.forEach((m) => m.dispose());
      else meshMat.dispose();
      const beamMat = pick.beam.material;
      if (Array.isArray(beamMat)) beamMat.forEach((m) => m.dispose());
      else beamMat.dispose();
    }
    this.pickups.length = 0;
    this.bobPhase.length = 0;
    this.beamGeo.dispose();
  }
}
